import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  afterEveryRender,
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { LocaleService, TranslatePipe } from '@cmi/addin-core';
import { MailMetadata } from '../mail-upload/filing-source';
import { DocumentField, MailAddress } from '../mail-upload/mail-upload.models';
import {
  FieldConfigField,
  FieldConfigResponse,
  FieldPrefillResponse,
  LocalizedLabel
} from './document-fields.models';

/** Values the user entered, keyed by field id. Dates are held as `YYYY-MM-DD`. */
export type FieldValues = Record<number, string>;

export type FieldKind = 'text' | 'date' | 'select' | 'unsupported';

export interface FieldValueChange {
  fieldId: number;
  value: string;
}

/** The parts of the open mail a field can be prefilled from, as MailService reads them. */
export interface PrefillMail {
  betreff: string;
  absender: MailAddress;
  /** ISO timestamp; null when Outlook has none. */
  gesendetDatum: string | null;
}

/** The prefill parts of a mail, from its subject and the metadata read with it. */
export function prefillMail(
  subject: string,
  { absender, gesendetDatum }: Omit<PrefillMail, 'betreff'>
): PrefillMail {
  return { betreff: subject, absender, gesendetDatum };
}

type PrefillReader = (mail: PrefillMail) => string;

function senderText(sender: MailAddress): string {
  const name = sender.name.trim();
  const email = sender.email.trim();

  return name && email ? `${name} <${email}>` : name || email;
}

/** An ISO timestamp as a calendar day in the user's time zone, laid out by `format`. */
function calendarDay(
  timestamp: string | null,
  format: (year: string, month: string, day: string) => string
): string {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  const pad = (value: number): string => String(value).padStart(2, '0');

  return format(String(date.getFullYear()), pad(date.getMonth() + 1), pad(date.getDate()));
}

/** A date of the mail as a date field takes it, and as a text field the Swiss way. */
function dateReaders<Mail>(
  read: (mail: Mail) => string | null
): Partial<Record<FieldKind, (mail: Mail) => string>> {
  return {
    date: (mail): string =>
      calendarDay(read(mail), (year, month, day): string => `${year}-${month}-${day}`),
    text: (mail): string =>
      calendarDay(read(mail), (year, month, day): string => `${day}.${month}.${year}`)
  };
}

/**
 * What each `quelle` reads off the mail, per kind of field it may go into. A
 * kind missing here is a mismatch: a subject in a date field, a sender in a
 * selection.
 */
const PREFILL_READERS = new Map<string, Partial<Record<FieldKind, PrefillReader>>>([
  ['betreff', { text: (mail): string => mail.betreff }],
  ['absender', { text: (mail): string => senderText(mail.absender) }],
  ['gesendetDatum', dateReaders((mail: PrefillMail) => mail.gesendetDatum)]
]);

/** The parts of a mail a placeholder can stand for, as the user picks them. */
export type PlaceholderKey = 'betreff' | 'absender' | 'gesendet' | 'empfangen';

/** The parts of a mail the placeholders read: those of the prefill, and the send time. */
export interface PlaceholderMail extends PrefillMail {
  /** ISO timestamp from the mail's Date header; null where the client cannot read it. */
  sentDateTime: string | null;
}

/** The placeholder parts of a mail as the filing pipeline read it. */
export function placeholderMail(metadata: MailMetadata): PlaceholderMail {
  return { ...prefillMail(metadata.subject, metadata), sentDateTime: metadata.sentDateTime };
}

/**
 * What each placeholder reads off the mail, per kind of field that takes it.
 * A kind missing here does not offer the placeholder: a subject in a date field.
 */
const PLACEHOLDER_READERS: Record<
  PlaceholderKey,
  Partial<Record<FieldKind, (mail: PlaceholderMail) => string>>
> = {
  betreff: { text: (mail): string => mail.betreff.trim() },
  absender: { text: (mail): string => senderText(mail.absender) },
  // Where the client cannot read the Date header, the arrival is the closest there is.
  gesendet: dateReaders((mail: PlaceholderMail) => mail.sentDateTime ?? mail.gesendetDatum),
  // gesendetDatum is Outlook's creation time, which for a received mail is its arrival.
  empfangen: dateReaders((mail: PlaceholderMail) => mail.gesendetDatum)
};

const PLACEHOLDER_KEYS = Object.keys(PLACEHOLDER_READERS) as PlaceholderKey[];

/** A placeholder as it is stored in a value: its key in braces. */
const PLACEHOLDER_TOKEN = new RegExp(`\\{(${PLACEHOLDER_KEYS.join('|')})\\}`, 'g');

/** The placeholders a field of this kind takes: any in a text field, a date in a date field. */
export function placeholdersFor(kind: FieldKind): PlaceholderKey[] {
  return PLACEHOLDER_KEYS.filter((key) => PLACEHOLDER_READERS[key][kind] !== undefined);
}

/** The placeholder a date field holds in place of a date, or null for a date or nothing. */
export function datePlaceholder(value: string): PlaceholderKey | null {
  const key = PLACEHOLDER_KEYS.find((candidate) => value.trim() === `{${candidate}}`);

  return key && PLACEHOLDER_READERS[key].date ? key : null;
}

/** Whether a value holds a placeholder, which filing replaces mail by mail. */
export function hasPlaceholders(value: string): boolean {
  return value.search(PLACEHOLDER_TOKEN) !== -1;
}

/**
 * Alles in geschweiften Klammern auf einer Zeile, was Benutzende vermutlich als
 * Platzhalter meinen. Klammern über mehrere Zeilen eines mehrzeiligen Felds
 * sind gewöhnlicher Text.
 */
const BRACED = /\{[^{}\n]*\}/g;

/** A piece of a text field's value: plain text, a placeholder, or braces that are none. */
export interface PlaceholderSegment {
  text: string;
  placeholder: 'known' | 'unknown' | null;
}

/**
 * A value cut into what the user sees: plain text, and each pair of braces as
 * a known placeholder by its name or as an unknown one, which filing keeps as
 * text. Joined up, the segments read as {@link showPlaceholders}.
 */
export function placeholderSegments(
  value: string,
  name: (key: PlaceholderKey) => string
): PlaceholderSegment[] {
  const segments: PlaceholderSegment[] = [];
  let end = 0;

  for (const match of value.matchAll(BRACED)) {
    const key = PLACEHOLDER_KEYS.find((candidate) => match[0] === `{${candidate}}`);

    if (match.index > end) {
      segments.push({ text: value.slice(end, match.index), placeholder: null });
    }

    segments.push(
      key
        ? { text: `{${name(key)}}`, placeholder: 'known' }
        : { text: match[0], placeholder: 'unknown' }
    );
    end = match.index + match[0].length;
  }

  if (end < value.length) {
    segments.push({ text: value.slice(end), placeholder: null });
  }

  return segments;
}

/** A value with each placeholder written as `{name}`, the way the user reads it. */
export function showPlaceholders(value: string, name: (key: PlaceholderKey) => string): string {
  return value.replace(PLACEHOLDER_TOKEN, (_token, key: PlaceholderKey) => `{${name(key)}}`);
}

/** Back from {@link showPlaceholders}: each `{name}` the user sees, stored as its key. */
export function storePlaceholders(text: string, name: (key: PlaceholderKey) => string): string {
  return PLACEHOLDER_KEYS.reduce(
    (stored, key) => stored.split(`{${name(key)}}`).join(`{${key}}`),
    text
  );
}

/** Which input a field gets. Any notice means the server could not fully resolve it. */
export function fieldKind(field: FieldConfigField): FieldKind {
  if (field.notice !== null) {
    return 'unsupported';
  }

  switch (field.type) {
    case 'Text':
      return 'text';
    case 'Date':
      return 'date';
    case 'Enum':
    case 'Association':
      return 'select';
    default:
      return 'unsupported';
  }
}

function allFields(config: FieldConfigResponse): FieldConfigField[] {
  return config.sections.flatMap((section) => section.columns.flatMap((column) => column.fields));
}

function filledValue(values: FieldValues, fieldId: number): string {
  return (values[fieldId] ?? '').trim();
}

/**
 * How a prefill entry fills its field, or why it cannot: an unknown `quelle`,
 * a field not in the mask, a field of the wrong kind.
 */
function prefillReader(
  fields: ReadonlyMap<number, FieldConfigField>,
  { feldId, quelle }: FieldPrefillResponse['felder'][number]
): { read: PrefillReader } | { skipped: string } {
  const readers = PREFILL_READERS.get(quelle);
  const field = fields.get(feldId);

  if (!readers) {
    return { skipped: 'the add-in does not know that source' };
  }

  if (!field) {
    return { skipped: 'it is not in the mask' };
  }

  const kind = fieldKind(field);
  const read = readers[kind];

  return read ? { read } : { skipped: `it is a ${kind} field` };
}

/** The label in the UI language, falling back to German, then to whatever there is. */
export function localizedLabel(label: LocalizedLabel, locale: string): string {
  return label[locale] ?? label['de'] ?? Object.values(label)[0] ?? '';
}

/** The mask with only the fields kept, and without the columns and sections that leaves empty. */
function keepFields(
  config: FieldConfigResponse,
  keep: (field: FieldConfigField) => boolean
): FieldConfigResponse {
  return {
    ...config,
    sections: config.sections
      .map((section) => ({
        ...section,
        columns: section.columns
          .map((column) => ({ ...column, fields: column.fields.filter(keep) }))
          .filter((column) => column.fields.length > 0)
      }))
      .filter((section) => section.columns.length > 0)
  };
}

/**
 * The mask without the fields the add-in cannot offer, and without the columns
 * and sections that leaves empty. They are neither shown, nor checked as
 * mandatory, nor sent: nobody could fill them in here anyway. Each field
 * dropped for a notice goes to the console, so support can tell why it is
 * missing from the mask.
 */
export function withoutUnsupportedFields(config: FieldConfigResponse): FieldConfigResponse {
  for (const field of allFields(config)) {
    if (field.notice !== null) {
      console.warn(
        `Field ${field.fieldId} (${localizedLabel(field.label, 'de')}) is not shown, the server flagged it with a notice:`,
        field.notice
      );
    }
  }

  return keepFields(config, (field) => fieldKind(field) !== 'unsupported');
}

/**
 * The fields each mail fills in itself, for filing several mails at once:
 * their values differ from mail to mail, so they are not asked for once for
 * all. A prefill the add-in cannot apply leaves its field to the user.
 */
export function prefilledFieldIds(
  config: FieldConfigResponse,
  prefill: FieldPrefillResponse
): ReadonlySet<number> {
  const fields = new Map(allFields(config).map((field) => [field.fieldId, field]));

  return new Set(
    prefill.felder
      .filter((entry) => 'read' in prefillReader(fields, entry))
      .map(({ feldId }) => feldId)
  );
}

/**
 * The `felder` of the Neu request: every field with a value. Dates go over as
 * ISO without a zone, which is the format the reference asks for. Expects a
 * mask that went through `withoutUnsupportedFields`.
 */
export function toFelder(config: FieldConfigResponse, values: FieldValues): DocumentField[] {
  return allFields(config)
    .map((field) => ({ field, value: filledValue(values, field.fieldId) }))
    .filter(({ value }) => value !== '')
    .map(({ field, value }) => ({
      feldId: field.fieldId,
      wert: fieldKind(field) === 'date' ? `${value}T00:00:00` : value
    }));
}

/**
 * The values the open mail prefills, keyed by field id like the user's own.
 * Expects a mask that went through `withoutUnsupportedFields`. An entry that
 * cannot be applied — an unknown `quelle`, a field not in the mask, a field of
 * the wrong kind — goes to the console, so support can tell why a field the
 * Designer binds to the mail starts out empty. Empty mail parts are left out.
 */
export function prefillValues(
  config: FieldConfigResponse,
  prefill: FieldPrefillResponse,
  mail: PrefillMail
): FieldValues {
  const fields = new Map(allFields(config).map((field) => [field.fieldId, field]));
  const values: FieldValues = {};

  for (const entry of prefill.felder) {
    const { feldId, quelle } = entry;
    const reader = prefillReader(fields, entry);

    if ('skipped' in reader) {
      console.warn(`Field ${feldId} is not prefilled from \`${quelle}\`: ${reader.skipped}`);
      continue;
    }

    const value = reader.read(mail).trim();

    if (value !== '') {
      values[feldId] = value;
    }
  }

  return values;
}

/**
 * The values with each placeholder replaced by what this mail says: anywhere
 * in a text field, in a date field when it is all the field holds. A part the
 * mail lacks leaves its spot empty, so a mandatory field left empty that way
 * fails this mail alone, like an empty prefill.
 */
export function resolvePlaceholders(
  config: FieldConfigResponse,
  values: FieldValues,
  mail: PlaceholderMail
): FieldValues {
  const resolved: FieldValues = { ...values };

  for (const field of allFields(config)) {
    const value = values[field.fieldId] ?? '';
    const kind = fieldKind(field);
    const dateKey = kind === 'date' ? datePlaceholder(value) : null;

    if (kind === 'text') {
      resolved[field.fieldId] = value.replace(
        PLACEHOLDER_TOKEN,
        (_token, key: PlaceholderKey) => PLACEHOLDER_READERS[key].text?.(mail) ?? ''
      );
    } else if (dateKey) {
      resolved[field.fieldId] = PLACEHOLDER_READERS[dateKey].date?.(mail) ?? '';
    }
  }

  return resolved;
}

/** Mandatory fields still empty. The server rejects the request while there are any. */
export function missingMandatory(
  config: FieldConfigResponse,
  values: FieldValues
): FieldConfigField[] {
  return allFields(config).filter(
    (field) => !field.optional && filledValue(values, field.fieldId) === ''
  );
}

@Component({
  selector: 'cmi-document-fields',
  imports: [NgClass, NgTemplateOutlet, TranslatePipe],
  templateUrl: './document-fields.html',
  // A click anywhere outside it closes an open placeholder menu.
  host: { '(document:click)': 'onDocumentClick($event)' }
})
export class DocumentFields {
  private readonly _localeService = inject(LocaleService);

  public readonly config = input.required<FieldConfigResponse>();
  public readonly values = input.required<FieldValues>();
  /**
   * Fields each mail fills in itself when several are filed at once. Shown
   * read-only and without a value, since that differs from mail to mail.
   */
  public readonly locked = input<ReadonlySet<number>>(new Set());
  /** What the mail fills in. A prefilled field offers no placeholders, the prefill says what goes there. */
  public readonly prefill = input<FieldPrefillResponse>({ felder: [] });
  public readonly valueChange = output<FieldValueChange>();

  protected readonly kind = fieldKind;
  protected readonly datePlaceholder = datePlaceholder;
  protected readonly hasPlaceholders = hasPlaceholders;

  /** The field whose placeholder menu is open, if any. */
  protected readonly openMenu = signal<number | null>(null);
  /** Ob das offene Menü nach oben aufgeht, weil unten kein Platz ist. */
  protected readonly menuUp = signal(false);
  private readonly _menu = viewChild<ElementRef<HTMLElement>>('menu');

  private readonly _prefilled = computed(() => prefilledFieldIds(this.config(), this.prefill()));

  /** The name of a placeholder in the UI language, as the user reads and types it. */
  protected readonly placeholderName = (key: PlaceholderKey): string =>
    this._localeService.translate(`documentFields.placeholders.${key}`);

  /**
   * Fields the user has been in. An empty mandatory field is only called out
   * once touched: the mask opens empty, and flagging it all red before anyone
   * typed would read as a mistake nobody made. The disabled button still holds
   * the upload back meanwhile.
   */
  private readonly _touched = signal<ReadonlySet<number>>(new Set());

  /** Jedes Textfeld hat genau eine Ebene und ein Eingabefeld, deshalb gehören gleiche Indizes zusammen. */
  private readonly _backdrops = viewChildren<ElementRef<HTMLElement>>('backdrop');
  private readonly _textInputs =
    viewChildren<ElementRef<HTMLInputElement | HTMLTextAreaElement>>('input');

  public constructor() {
    // Die Ebene ist ohne Markierung ausgeblendet und nimmt dann keine Scroll-Position an.
    // Erscheint sie durch Einfügen oder Paste ohne Scroll- oder Tastatur-Event, steht sie
    // sonst oben, während das Feld weiter unten gescrollt ist.
    afterEveryRender(() => {
      const textInputs = this._textInputs();

      this._backdrops().forEach((backdrop, index) =>
        this.syncScroll(textInputs[index].nativeElement, backdrop.nativeElement)
      );
    });

    // Das Menü geht nach unten auf. Liegt seine Unterkante unter dem Ablage-Button oder
    // ausserhalb des Taskpane, geht es stattdessen nach oben auf.
    afterRenderEffect(() => {
      const menu = this._menu()?.nativeElement;

      // jsdom kennt elementFromPoint nicht.
      if (!menu || this.menuUp() || !document.elementFromPoint) {
        return;
      }

      const rect = menu.getBoundingClientRect();
      const atLowerEdge = document.elementFromPoint(rect.left + rect.width / 2, rect.bottom - 1);

      if (!menu.contains(atLowerEdge)) {
        this.menuUp.set(true);
      }
    });
  }

  protected label(label: LocalizedLabel): string {
    return localizedLabel(label, this._localeService.currentLocale());
  }

  protected value(field: FieldConfigField): string {
    return this.values()[field.fieldId] ?? '';
  }

  /** The value as the input shows it, each placeholder by its name. */
  protected shownValue(field: FieldConfigField): string {
    return showPlaceholders(this.value(field), this.placeholderName);
  }

  /** What a text field shows, its placeholders marked as known or not. */
  protected segments(field: FieldConfigField): PlaceholderSegment[] {
    return placeholderSegments(this.value(field), this.placeholderName);
  }

  /** The braces of a text field that are no placeholder, as typed, for the warning. */
  protected unknownPlaceholders(segments: PlaceholderSegment[]): string {
    return segments
      .filter((segment) => segment.placeholder === 'unknown')
      .map((segment) => segment.text)
      .join(', ');
  }

  /** Keeps the marks under the text when a long value scrolls inside its input. */
  protected syncScroll(
    textInput: HTMLInputElement | HTMLTextAreaElement,
    backdrop: HTMLElement
  ): void {
    backdrop.scrollLeft = textInput.scrollLeft;
    backdrop.scrollTop = textInput.scrollTop;
  }

  protected placeholders(field: FieldConfigField): PlaceholderKey[] {
    return this._prefilled().has(field.fieldId) ? [] : placeholdersFor(fieldKind(field));
  }

  protected isMissing(field: FieldConfigField): boolean {
    return !field.optional && this._touched().has(field.fieldId) && !this.value(field).trim();
  }

  protected onInput(field: FieldConfigField, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)
      .value;
    this.onTouch(field);
    this.valueChange.emit({
      fieldId: field.fieldId,
      value: fieldKind(field) === 'text' ? storePlaceholders(value, this.placeholderName) : value
    });
  }

  protected toggleMenu(field: FieldConfigField): void {
    this.openMenu.update((open) => (open === field.fieldId ? null : field.fieldId));
    this.menuUp.set(false);
  }

  /**
   * Puts the placeholder where the cursor was in a text field, keeping what is
   * there. A date field, which comes without its input, takes it in place of its date.
   */
  protected onPlaceholder(
    field: FieldConfigField,
    key: PlaceholderKey,
    textInput: HTMLInputElement | HTMLTextAreaElement | null
  ): void {
    this.openMenu.set(null);
    this.onTouch(field);

    if (!textInput) {
      this.valueChange.emit({ fieldId: field.fieldId, value: `{${key}}` });
      return;
    }

    const token = `{${this.placeholderName(key)}}`;
    const start = textInput.selectionStart ?? textInput.value.length;
    const end = textInput.selectionEnd ?? start;
    const text = textInput.value.slice(0, start) + token + textInput.value.slice(end);

    // Set here rather than through the binding, so the cursor can go right after the placeholder.
    textInput.value = text;
    textInput.focus();
    textInput.setSelectionRange(start + token.length, start + token.length);
    this.valueChange.emit({
      fieldId: field.fieldId,
      value: storePlaceholders(text, this.placeholderName)
    });
  }

  protected removePlaceholder(field: FieldConfigField): void {
    this.onTouch(field);
    this.valueChange.emit({ fieldId: field.fieldId, value: '' });
  }

  protected onMenuEscape(button: HTMLButtonElement): void {
    this.openMenu.set(null);
    button.focus();
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (!(event.target as Element | null)?.closest('[data-placeholder-menu]')) {
      this.openMenu.set(null);
    }
  }

  protected onTouch(field: FieldConfigField): void {
    if (!this._touched().has(field.fieldId)) {
      this._touched.update((touched) => new Set(touched).add(field.fieldId));
    }
  }
}
